/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {translate} from '@ion/core/src/locale/locale';
import {IonSnackbar} from '@ion/dls/src/notifications/IonSnackbar';
import {toggleUserPrompt} from '@/store/actions/user-prompt';
import {notificationSelector} from '@/store/selectors/user-prompt-selectors';

interface UserPromptProps {
  isFromLogin?: boolean
};

/**
 * @GlobalUserPrompt
 *
 * This is a user alert component that will display a snack
 * if the action is dispatched to display the IonSnackbar
 */
const GlobalUserPrompt = ({
  isFromLogin = false
}: UserPromptProps) => {
  const {
    display: displaySnack,
    text: snackText,
    type,
    maxWidth,
    translateOption,
    actionLabel,
    autoHide,
    customDismissLabel,
    onDismiss,
    onAction,
    autoHideDuration,
    partialSuccessState,
    displayOnlyInLayout
  } = useSelector(notificationSelector);
  const dispatch = useDispatch();
  const handleClose = useCallback(function() {
    dispatch(toggleUserPrompt({display: false, displayOnlyInLayout: false}));
  }, []);

  if (displayOnlyInLayout ? !isFromLogin : true) {
    return (
      <IonSnackbar show={displaySnack}
        // i18n keys in this codebase are always a bare `namespace:dotted.path`
        // with no spaces; snackText is sometimes that, and sometimes an
        // already-final, human-readable message (e.g. a raw backend error
        // returned verbatim by formatErrorMessage). Passing the latter
        // through translate() lets i18next parse its first colon as a
        // namespace separator, mangling any message with a colon in it (a
        // time value, for example) - so only translate values that actually
        // look like keys ...
        message={snackText?.includes(' ') ? snackText : translate(snackText, translateOption)}
        actionLabel={translate(actionLabel)}
        customDismissLabel={translate(customDismissLabel)}
        autoHideDuration={autoHide ? autoHideDuration : null}
        partialSuccessState={partialSuccessState}
        onAction={onAction || handleClose}
        onDismiss={onDismiss}
        onClose={handleClose}
        type={type}
        maxWidth={maxWidth}/>
    );
  };
};

export default memo(GlobalUserPrompt);
